import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

export const Image = ({
  className,
  src,
  alt,
  lazy,
  onLoad,
  width,
  height,
  formats,
  style,
}) => {
  const ref = useRef();
  const [isMobile, setIsMobile] = useState(false);
  let timeout = undefined

  useEffect(() => {
    setIsMobile(window.innerWidth <= 768);
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const [loaded, setLoaded] = useState(false);

  const onImageLoaded = useCallback(() => {
    setLoaded(true);
    if (onLoad) onLoad();
  }, [onLoad]);

  useEffect(() => {
    clearTimeout(timeout)

    timeout = setTimeout(() => {
      setLoaded(true);
      if (onLoad) onLoad();
    }, 500)

    if (ref.current.complete) {
      clearTimeout(timeout)
      setLoaded(true);
      if (onLoad) onLoad();
    }
  }, [ref.current, onLoad]);

  let finalSrc = src;

  if (typeof src === 'object') {
    if (isMobile && src.mobile) {
      finalSrc = src.mobile;
    } else {
      finalSrc = src.desktop;
    }
  }

  return (
    <>
      {
        !formats
          ? (
            <img
              ref={ref}
              className={(`Image ${lazy ? (loaded ? 'Image--loaded' : 'Image--loading') : ''} ${className || ''}`).trim()}
              src={finalSrc}
              alt={alt}
              loading={lazy ? 'lazy' : 'eager'}
              onLoad={onImageLoaded}
              width={width}
              height={height}
              style={style}
            />
          )
          : (
            <picture>
              {
                formats.slice(0, formats.length - 1).map((item, index) => {
                  return (
                    <source
                      key={index}
                      media={`(max-width: ${item.width}px)`}
                      srcSet={item.src}
                    />
                  );
                })
              }
              <img
                ref={ref}
                className={(`Image ${lazy ? (loaded ? 'Image--loaded' : 'Image--loading') : ''} ${className || ''}`).trim()}
                src={formats[formats.length - 1].src}
                alt={alt}
                loading={lazy ? 'lazy' : 'eager'}
                onLoad={onImageLoaded}
                width={width}
                height={height}
                style={style}
              />
            </picture>
          )
      }
    </>
  );
};

Image.propTypes = {
  className: PropTypes.string,
  src: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      desktop: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      mobile: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }),
  ]).isRequired,
  alt: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lazy: PropTypes.bool,
  onLoad: PropTypes.func,
  style: PropTypes.object,
  formats: PropTypes.array,
};

Image.defaultProps = {
  className: undefined,
  alt: '',
  lazy: false,
  onLoad: undefined,
  width: undefined,
  height: undefined,
  style: undefined,
  formats: undefined,
};
